import { useEffect, useMemo, useState } from 'react';
import { pluralize } from 'utils/shared/pluralize';
import { useConnections } from 'hooks/useConnections';
import Plus from 'svg/Plus';
import Button from 'components/Button';
import DetailModalShell from 'components/Find/DetailModalShell';
import PlayerRow from './PlayerRow';

interface Props {
  isOpen: boolean;
  /** Ids already invited — these sort to the top and show "Remove". */
  selectedPlayerIds: string[];
  /**
   * Invites that can no longer be withdrawn here, because the player accepted and
   * now holds a real seat. Shown as confirmed rather than removable.
   */
  lockedPlayerIds?: string[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (playerIds: string[]) => void | Promise<void>;
}

export default function InvitePlayersModal({
  isOpen,
  selectedPlayerIds,
  lockedPlayerIds = [],
  isSubmitting = false,
  onClose,
  onSubmit,
}: Props) {
  const { connections } = useConnections();
  const [draftIds, setDraftIds] = useState<string[]>(selectedPlayerIds);

  // Reopening starts from what the form currently holds, discarding an abandoned edit.
  useEffect(() => {
    if (isOpen) {
      setDraftIds(selectedPlayerIds);
    }
  }, [isOpen, selectedPlayerIds]);

  // Selected first, then the rest — both keeping the connections order.
  const orderedConnections = useMemo(() => {
    const selected = new Set(draftIds);
    return [
      ...connections.filter((player) => selected.has(player.id)),
      ...connections.filter((player) => !selected.has(player.id)),
    ];
  }, [connections, draftIds]);

  const toggle = (playerId: string) => {
    if (lockedPlayerIds.includes(playerId)) return;
    setDraftIds((ids) =>
      ids.includes(playerId) ? ids.filter((id) => id !== playerId) : [...ids, playerId],
    );
  };

  const footer = (
    <div className="flex gap-ds-lg">
      <Button
        variant="secondary"
        size="lg"
        onClick={onClose}
        disabled={isSubmitting}
        className="flex-1"
      >
        Cancel
      </Button>
      <Button
        variant="brand"
        size="lg"
        loading={isSubmitting}
        onClick={() => onSubmit(draftIds)}
        className="flex-1"
      >
        Submit
      </Button>
    </div>
  );

  return (
    <DetailModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Invite players"
      footer={footer}
      hasFooterDivider
    >
      <h3 className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        {`${pluralize({ count: connections.length, singular: 'Connection', hideCount: true })} (${
          connections.length
        })`}
      </h3>
      <ul className="mt-ds-md divide-y divide-color-border-input-lightmode dark:divide-color-border-input-darkmode">
        {orderedConnections.map((player) => {
          const isSelected = draftIds.includes(player.id);
          const isLocked = lockedPlayerIds.includes(player.id);

          return (
            <li key={player.id} className="py-ds-sm">
              <PlayerRow
                player={player}
                action={
                  <Button
                    variant={isSelected ? 'secondary' : 'primary'}
                    size="xs"
                    isInline
                    disabled={isLocked}
                    onClick={() => toggle(player.id)}
                    iconLeft={isSelected || isLocked ? undefined : <Plus className="size-3" />}
                    className="shrink-0"
                  >
                    {isLocked ? 'Joined' : isSelected ? 'Remove' : 'Add'}
                  </Button>
                }
              />
            </li>
          );
        })}
      </ul>
    </DetailModalShell>
  );
}
